"use client";

import { FC, useState } from "react";
import { MdDelete } from "react-icons/md";
import PagedCloudImage from "@/app/_lib/components/cloudinary/paged-cloud-image";
import { DocumentTypes, Vendor } from "@/prisma-package/src";
import DocumentViewModal from "@/app/_lib/components/shared/document-view-modal";
import Modal from "@/app/_lib/components/modals/modal";
import { deleteDocumentAction } from "@/app/_lib/actions/document.actions";
import { useToast } from "@/app/_lib/hooks/use-toast";

type Props = {
  width: number;
  height: number;
  src: string;
  alt: string;
  documentType?: string;
  onDelete?: () => void;
  projectId?: string;
  documentId?: string;
  viewOnly?: boolean;
  vendors?: Vendor[];
  allowedDocumentTypes?: Record<string, string>;
};

const CloudImage: FC<Props> = ({
  width,
  height,
  src,
  alt,
  onDelete,
  documentType,
  projectId,
  documentId,
  viewOnly,
  vendors,
  allowedDocumentTypes,
}) => {
  const toast = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClick = () => {
    setIsModalOpen(!isModalOpen);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (documentId && projectId) {
        const [err] = await deleteDocumentAction({
          docId: documentId,
          projectId,
        });

        if (err) {
          toast({
            message: err.message,
            type: "error",
            state: "active",
          });
        } else {
          toast({
            message: "Document deleted successfully",
            type: "success",
            state: "active",
          });
          onDelete?.();
        }
      } else {
        toast({
          message: "Missing docId or projectId",
          type: "error",
          state: "active",
        });
      }
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <div className="relative">
      <PagedCloudImage
        src={src}
        alt={alt}
        width={width}
        height={height}
        crop="fill"
        className={"w-auto h-auto cursor-pointer hover:opacity-80"}
        onClick={handleClick}
      />
      {onDelete && !viewOnly && (
        <button
          className="absolute top-0 left-0 text-white text-md p-2 bg-primary"
          type="button"
          onClick={() => setIsDeleteModalOpen(true)}
        >
          <MdDelete />
        </button>
      )}
      {isModalOpen && (
        <DocumentViewModal
          src={src}
          alt={alt}
          width={width}
          height={height}
          crop="fill"
          className={"w-auto h-auto"}
          documentType={documentType}
          projectId={projectId}
          documentId={documentId}
          allowedDocumentTypes={allowedDocumentTypes}
          isOpen={isModalOpen}
          onClose={handleClick}
          viewOnly={viewOnly}
          vendors={vendors}
        />
      )}
      {isDeleteModalOpen && (
        <Modal
          title="Delete Document"
          content="Are you sure you want to delete this document? This action cannot be undone."
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          buttons={[
            <button
              key="cancel"
              onClick={() => setIsDeleteModalOpen(false)}
              className="btn btn-ghost"
            >
              Cancel
            </button>,
            <button
              key="delete"
              onClick={handleDelete}
              disabled={isDeleting}
              className="btn btn-error"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>,
          ]}
        />
      )}
    </div>
  );
};

export default CloudImage;
